import ToolsButtonSVG from "../../Media/ToolsButton";
import styled from "styled-components";
import { useState, useRef } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import Tooltip from "@mui/material/Tooltip";
import { Menu, MenuItem, MenuProps, Typography } from "@mui/material";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { styled as muiStyled, alpha } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { centerGraph } from "../../utils/DiagramUtils";

const ToolsButtonWrapper = styled.div`
  position: fixed;
  top: 50vh; /* Moved up, maintaining 10vh spacing */
  left: -0.7vw;
  height: 5vw;
  width: 5vw;
  cursor: pointer;

  /* Hover states */
  &:hover .toolsIcon {
    fill: #ffaea6;
    color: #ffaea6;
  }
  &:hover .halfCircle {
    fill: white;
  }
  &:hover .pencil {
    fill: #ffb6a6;
  }
`;

const StyledMenu = muiStyled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "center",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "center",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 15,
    marginLeft: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const HelpIcon = styled(HelpOutlineSharpIcon)`
  color: #ff9999;
  margin-left: 8px;
  cursor: pointer;
  font-size: 18px; /* Made the question mark smaller */
`;

// Consistent menu item styling
const BaseMenuItem = muiStyled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  paddingLeft: "16px",
  paddingRight: "15px",
  height: "40px", // Fixed height for all menu items
  "&:hover": {
    backgroundColor: "rgba(255, 174, 166, 0.08)",
  },
}));

// Container for left-side icon with fixed width
const LeftIconContainer = muiStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px", // Increased fixed width for all left icons
  minWidth: "40px", // Ensure fixed width for alignment
  height: "24px",
}));

// Update the help icon to align right
const TooltipWrapper = muiStyled("div")(({ theme }) => ({
  marginLeft: "auto", // This pushes the tooltip to the far right
  display: "flex",
  alignItems: "center",
  paddingLeft: "20px", // Add padding to create more gap between text and help icon
}));

// Container for text content to ensure alignment
const TextContent = muiStyled("div")(({ theme }) => ({
  paddingLeft: "10px", // Reduced padding to make text closer to icons
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
  flex: "1", // Take available space but allow margin for the tooltip
}));

const ToolsButton: React.FC<any> = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCenterGraph = async () => {
    try {
      await centerGraph();
      handleClose(); // Close the menu after action is performed
    } catch (error) {
      console.error("Error centering graph:", error);
      enqueueSnackbar("Failed to center graph", { variant: "error" });
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Tools">
        <ToolsButtonWrapper
          className="ToolsButtonWrapper"
          onClick={handleClick}
          ref={buttonRef}
        >
          <ToolsButtonSVG />
          <SettingsIcon
            htmlColor="#ffffff"
            style={{
              position: "absolute",
              top: "30%",
              left: "18%",
              width: "30px",
              height: "30px",
            }}
            className="toolsIcon"
          />
        </ToolsButtonWrapper>
      </Tooltip>

      <StyledMenu
        id="tools-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <BaseMenuItem
          onClick={handleCenterGraph}
          data-testid="centerGraphToolMenuButton"
        >
          <LeftIconContainer>
            <CenterFocusStrongIcon
              fontSize="small"
              style={{ color: "#ff9999" }}
            />
          </LeftIconContainer>
          <TextContent>Center Graph</TextContent>
          <TooltipWrapper>
            <Tooltip
              title={
                <Typography variant="body2" style={{ fontSize: "13px" }}>
                  Centers the diagram view. If a cell is selected, centers on
                  that cell. Otherwise, shows all cells.
                </Typography>
              }
              arrow
            >
              <HelpIcon fontSize="small" />
            </Tooltip>
          </TooltipWrapper>
        </BaseMenuItem>
      </StyledMenu>
    </>
  );
};

export default ToolsButton;
